import type { KontaktRef } from "./mailAssignedContact";

/**
 * J2 — Sofortmeldung „diese Mail gehört jetzt zu diesem Kontakt".
 *
 * Die WAHRHEIT steht im persistierten `MailLink` und kommt beim nächsten Laden
 * über `GET /mail/links?conversationId=`. Dieser Bus ist nur die unmittelbare
 * Rückmeldung innerhalb desselben Tabs: die Mailliste zieht den Namen nach, und
 * ein zweites Fenster, das eine ANDERE Nachricht derselben Konversation zeigt,
 * folgt ohne Zutun. Modul-lokal statt `window`-Event, damit es im Test ohne DOM
 * benutzbar bleibt.
 *
 * P7 (PO 14.09.2026): derselbe Bus meldet auch „diese Mail hängt jetzt an
 * einem Vorgang" (`vorgang`), damit die Mailliste ihren Vorgangs-Marker ohne
 * Neuladen nachzieht. Eine Meldung trägt nur die Felder, über die sie etwas
 * sagt: `kontakt` FEHLT bei einer reinen Vorgangs-Meldung — `kontakt: null`
 * hieße „bewusst ohne Kontakt" und würde eine bestehende Zuweisung löschen.
 */

export type MailZuweisung = {
    mailbox: string;
    messageId: string;
    conversationId?: string | null;
    /** Kontakt der Mail; `undefined` = diese Meldung sagt nichts über den Kontakt. */
    kontakt?: KontaktRef | null;
    /**
     * P7 — die Mail hängt jetzt an diesem Vorgang (direkt) bzw. To-do. Nur das
     * Ziel, kein Titel: den holt sich die Liste über den Lookup, zusammen mit
     * der Sichtbarkeitsprüfung des Servers. `undefined` = keine Aussage.
     */
    vorgang?: { vorgangId: number | null; todoId: number | null };
};

type Hoerer = (z: MailZuweisung) => void;

const hoerer = new Set<Hoerer>();

export function publiziereZuweisung(z: MailZuweisung): void {
    for (const h of Array.from(hoerer)) {
        try {
            h(z);
        } catch {
            /* ein defekter Hörer darf die anderen nicht mitreißen */
        }
    }
}

export function abonniereZuweisung(fn: Hoerer): () => void {
    hoerer.add(fn);
    return () => {
        hoerer.delete(fn);
    };
}

/**
 * Betrifft die Meldung diese geöffnete Mail? Wahr bei gleicher
 * (Postfach, messageId) ODER gleicher, nicht-leerer `conversationId` — nur so
 * folgt auch ein Fenster, das eine andere Nachricht desselben Verlaufs zeigt.
 */
export function betrifft(
    z: MailZuweisung,
    ziel: { mailbox: string; messageId: string; conversationId?: string | null },
): boolean {
    const norm = (v: string | null | undefined) => String(v ?? "").trim().toLowerCase();
    if (norm(z.mailbox) === norm(ziel.mailbox) && z.messageId === ziel.messageId) return true;
    const a = String(z.conversationId ?? "").trim();
    const b = String(ziel.conversationId ?? "").trim();
    return a.length > 0 && a === b;
}
